// The external CMS generates Vuetify 2/3-era classes (row, col, container,
// col-lg-4, v-col-12, ...) that have no CSS in Vuetify 4. These rules add the V4
// equivalents and rename or drop the rest.
//
// They work on the markup string, not on the rendered DOM, so the server renders
// the migrated classes and the client hydrates the same string. Applied to the DOM
// at mount, every CMS grid was laid out without its grid CSS until hydration and
// then laid out again under the visitor.
//
// The stylesheets that depend on what these rules emit cannot import this, so a
// change to those classes is made there by hand too: v2-gutters is the app's own
// class (assets/scss/v2-compat.scss), and the CMS grid parity shim in
// assets/scss/global.scss sizes v-col--cols-* on portal pages.

import { cmsCarouselPlaceholders } from '~/utils/cmsContent'

// Known theme color names used in this project
const themeColors = [
  'primary', 'secondary', 'error', 'info', 'success', 'warning',
  'white', 'black', 'greyedOut', 'hover', 'header', 'demoHeader',
  'backgroundDiv', 'backgroundPage', 'teal', 'accent',
  'iconDesktop', 'border', 'successCheck', 'backgroundButton'
]

// 1:1 class renames
const classRenames: Record<string, string> = {
  // Theme
  'theme--light': 'v-theme--light',
  'theme--dark': 'v-theme--dark',
  'v-application--is-ltr': 'v-locale--is-ltr',
  'v-application--wrap': 'v-application__wrap',
  // Button
  'v-btn--text': 'v-btn--variant-text',
  'v-btn--outlined': 'v-btn--variant-outlined',
  'v-size--default': 'v-btn--size-default',
  'v-size--small': 'v-btn--size-small',
  // Icon
  'v-icon--link': 'v-icon--clickable',
  // Input
  'v-input--dense': 'v-input--density-compact',
  // Nav drawer
  'v-navigation-drawer--is-mobile': 'v-navigation-drawer--mobile',
  // Carousel
  'v-carousel__item': 'v-carousel-item',
  'v-image': 'v-img',
  // Breakpoints
  'hidden-xs-only': 'hidden-xs',
  'hidden-sm-only': 'hidden-sm',
}

// Classes to remove entirely: the eleven confirmed V2-only tokens with no V4
// equivalent, plus three removed without replacement — V4 only produces them
// through component props (hideDetails, VSelect's comma node, VToolbar
// density), which static CMS markup cannot reproduce.
const classRemovals = new Set([
  'v-btn--round', 'v-btn--router',
  'v-text-field--enclosed', 'v-text-field--filled',
  'v-text-field--full-width', 'v-text-field--placeholder',
  'v-text-field--rounded', 'v-text-field--single-line',
  'v-select__slot', 'v-select__selections',
  'v-navigation-drawer--close',
  'v-input--hide-details', 'v-select__selection--comma', 'v-toolbar--dense',
])

// Regex patterns for grid class detection
// V2: col-{bp}-{N} (e.g. col-sm-4), col-{N} (e.g. col-12)
const BP = 'xs|sm|md|lg|xl|xxl'
const colBpRegex = new RegExp(`^col-(${BP})-(\\d+|auto)$`)
const colBaseRegex = /^col-(\d+|auto)$/
// V3: v-col-{bp}-{N} (e.g. v-col-sm-4), v-col-{N} (e.g. v-col-12) — NOT v-col--cols-*
const v3ColBpRegex = new RegExp(`^v-col-(${BP})-(\\d+|auto)$`)
const v3ColBaseRegex = /^v-col-(\d+|auto)$/
// Row children: numeric widths only (col-auto does not make a row multi-column),
// and any breakpoint-prefixed col class (col-sm-offset-1 included).
const colNumericRegex = new RegExp(`^col-((${BP})-)?\\d+$`)
const colBpPrefixRegex = new RegExp(`^col-(${BP})-`)

// The classes one element ends up with, from its own and its element children's
// authored classes.
export const migrateClasses = (classes: readonly string[], childClasses: readonly (readonly string[])[]): string[] => {
  if (classes.length === 0) {
    return [...classes]
  }
  const has = (cls: string) => classes.includes(cls)
  const toAdd: string[] = []
  const toRemove: string[] = []

  // ── Grid: Add V4 classes alongside V2/V3 classes ──
  // V2 classes stay as dead weight (no CSS in V4), V4 classes do the actual layout.
  // V4's flex-basis formula accounts for gap, so no double-spacing issues.

  // ROW: "row" → add "v-row" + density class
  // DON'T add v-row if element has flex-column — v-row's display:flex overrides flex-direction
  // DON'T add v-row if element has no col-like children — text/header rows should stay block
  if (has('row') && !has('v-row') && !has('flex-column')) {
    // Only add v-row for meaningful multi-column layouts.
    // A row wrapping a single col-12 is just a block wrapper — v-row (display:flex, flex:1 1 auto)
    // would cause height collapse when stacked alongside other elements.
    const colChildren = childClasses.filter(c =>
      c.includes('col') ||
      c.some(cls => colNumericRegex.test(cls))
    )
    // Only add v-row if there are breakpoint cols (col-sm-4, etc.) or multiple col children
    const hasBreakpointCols = childClasses.some(c =>
      c.some(cls => colBpPrefixRegex.test(cls))
    )
    const isMultiCol = colChildren.length > 1

    if (hasBreakpointCols || isMultiCol) {
      toAdd.push('v-row')
      if (has('no-gutters')) {
        toAdd.push('v-row--no-gutters')
      } else {
        toAdd.push('v-row--density-default')
      }
    }
  }

  // CONTAINER: "container" → add "v-container"
  if (has('container') && !has('v-container')) {
    toAdd.push('v-container')
  }
  if (has('container--fluid') && !has('v-container--fluid')) {
    toAdd.push('v-container--fluid')
  }

  // COL: Detect col-like elements and add V4 equivalents
  // An element is "col-like" if it has: bare "col", "col-{N}", OR "col-{bp}-{N}"
  let isColLike = has('col')

  classes.forEach((cls) => {
    // V2: col-sm-4 → add v-col--cols-sm-4
    const bpMatch = cls.match(colBpRegex)
    if (bpMatch) {
      isColLike = true
      const v4Class = `v-col--cols-${bpMatch[1]}-${bpMatch[2]}`
      if (!has(v4Class)) {
        toAdd.push(v4Class)
      }
      return
    }

    // V2: col-12 → add v-col--cols-12
    const baseMatch = cls.match(colBaseRegex)
    if (baseMatch) {
      isColLike = true
      const v4Class = `v-col--cols-${baseMatch[1]}`
      if (!has(v4Class)) {
        toAdd.push(v4Class)
      }
      return
    }

    // V3: v-col-sm-4 → replace with v-col--cols-sm-4
    const v3BpMatch = cls.match(v3ColBpRegex)
    if (v3BpMatch) {
      isColLike = true
      toRemove.push(cls)
      toAdd.push(`v-col--cols-${v3BpMatch[1]}-${v3BpMatch[2]}`)
      return
    }

    // V3: v-col-12 → replace with v-col--cols-12
    const v3BaseMatch = cls.match(v3ColBaseRegex)
    if (v3BaseMatch) {
      isColLike = true
      toRemove.push(cls)
      toAdd.push(`v-col--cols-${v3BaseMatch[1]}`)
      return
    }

    // 1:1 renames
    // Own keys only: a class named after an Object.prototype member is not a rename.
    const renamed = Object.hasOwn(classRenames, cls) ? classRenames[cls] : undefined
    if (renamed) {
      toRemove.push(cls)
      toAdd.push(renamed)
      return
    }
    // v-btn--has-bg alone doesn't determine elevation: V2's default button
    // had a background AND an elevation shadow, while `flat`/`depressed`
    // kept the background but suppressed the shadow. Emit the matching V4
    // variant from the companion source classes.
    if (cls === 'v-btn--has-bg') {
      toRemove.push(cls)
      const flatOrDepressed = has('v-btn--flat') || has('v-btn--depressed')
      toAdd.push(flatOrDepressed ? 'v-btn--variant-flat' : 'v-btn--variant-elevated')
      return
    }

    // Classes to remove entirely
    if (classRemovals.has(cls)) {
      toRemove.push(cls)
      return
    }

    // Color: {color}--text → text-{color}
    if (cls.endsWith('--text')) {
      const color = cls.slice(0, -6)
      if (themeColors.includes(color)) {
        toRemove.push(cls)
        toAdd.push(`text-${color}`)
      }
      return
    }

    // Color: bare {color} as bg → bg-{color}
    if (themeColors.includes(cls) && !cls.startsWith('text-') && !cls.startsWith('bg-')) {
      toRemove.push(cls)
      toAdd.push(`bg-${cls}`)
    }
  })

  // Ensure col-like elements have the v-col base class
  if (isColLike && !has('v-col') && !toAdd.includes('v-col')) {
    toAdd.push('v-col')
  }

  // Density/size classes for V4 components (already present v-row/v-btn/v-icon)
  if (has('v-row') && !toAdd.includes('v-row')) {
    // Element already had v-row (not added by us) — ensure density
    if (!has('v-row--density-default') && !has('v-row--density-compact')
      && !has('v-row--no-gutters')
      && !toAdd.includes('v-row--density-default') && !toAdd.includes('v-row--no-gutters')) {
      toAdd.push('v-row--density-default')
    }
  }

  // Default-gutter rows from V2 CMS markup (raw `row` class) — whether v-row
  // was added above or upstream by Sitefinity's class conversion — were
  // authored against the V2 coupled gutter model (row margin -12px + col
  // padding 12px, `.row + .row` 12px). v2-gutters (v2-compat.scss) restores
  // it; V4's density-default renders margin 0 with gap-based cols.
  if (has('row')
    && (has('v-row') || toAdd.includes('v-row'))
    && !has('no-gutters') && !has('v-row--no-gutters')
    && !has('v2-gutters')) {
    toAdd.push('v2-gutters')
  }

  if (has('v-btn') && !has('v-btn--density-default') && !has('v-btn--density-compact')) {
    toAdd.push('v-btn--density-default')
  }

  if (has('v-icon') && !has('v-icon--size-default') && !has('v-icon--size-small')) {
    toAdd.push('v-icon--size-default')
  }

  // As classList.remove then classList.add would leave them: each class once,
  // authored order first, added ones after.
  const result = classes.filter((cls, index) => classes.indexOf(cls) === index && !toRemove.includes(cls))
  toAdd.forEach((cls) => {
    if (!result.includes(cls)) {
      result.push(cls)
    }
  })
  return result
}

// ── Markup ──
// Enough of the HTML tree to know each element's element children: start and end
// tags (quote-aware, as in cmsContent.ts), void elements, comments, raw-text
// elements, and the implied ends CMS markup relies on (<p> before a block, <li>
// before <li>). Anything else is taken as written.
const TOKEN = /<!--[\s\S]*?(?:-->|$)|<(\/?)([a-zA-Z][^\s/>]*)((?:"[^"]*"|'[^']*'|[^>"'])*)>/g
const ATTRIBUTE = /([^\s"'=/>][^\s"'=>]*)(?:\s*=\s*("[^"]*"|'[^']*'|[^\s>]+))?/g
const VOID = new Set(['area', 'base', 'br', 'col', 'embed', 'hr', 'img', 'input', 'link', 'meta', 'param', 'source', 'track', 'wbr'])
const RAW_TEXT = ['script', 'style', 'textarea', 'title', 'xmp', 'iframe', 'noembed', 'noframes', 'noscript']
// Raw text ends at the first end tag of its own name: `</`, the name, then
// whitespace, `/` or `>` (`</textareax>` is text). Matched case-insensitively in
// place: a lowercased copy can differ in length (U+0130 lowercases to two code
// units), and an index into it would be off.
const RAW_TEXT_END = new Map(RAW_TEXT.map(name => [name, new RegExp(`</${name}[\\t\\n\\f\\r />]`, 'gi')]))
const CLOSES_P = new Set([
  'address', 'article', 'aside', 'blockquote', 'details', 'div', 'dl', 'fieldset', 'figcaption', 'figure', 'footer',
  'form', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'header', 'hgroup', 'hr', 'main', 'menu', 'nav', 'ol', 'p', 'pre',
  'section', 'table', 'ul'
])
const P_SCOPE_BOUNDARY = new Set(['button', 'table', 'td', 'th', 'caption', 'template', 'svg', 'math', 'object'])
const LIST_SCOPE_BOUNDARY = new Set(['ul', 'ol', ...P_SCOPE_BOUNDARY])
const NO_SCOPE_BOUNDARY = new Set<string>()

interface MarkupElement {
  name: string
  // Where the start tag begins in the markup.
  start: number
  classes: string[]
  // Where the class attribute's value sits in the markup, quotes included.
  classValue?: { start: number, end: number, quote: string }
  children: MarkupElement[]
}

const readStartTag = (name: string, start: number, attributes: string, attributesStart: number): MarkupElement => {
  const element: MarkupElement = { name, start, classes: [], children: [] }
  let classSeen = false
  for (const match of attributes.matchAll(ATTRIBUTE)) {
    const attributeName = match[1]!.toLowerCase()
    // The first class attribute is the one the browser keeps.
    if (attributeName !== 'class' || classSeen) {
      continue
    }
    classSeen = true
    const raw = match[2]
    if (raw === undefined) {
      continue
    }
    const quote = raw[0] === '"' || raw[0] === "'" ? raw[0] : ''
    const start = attributesStart + match.index + match[0].length - raw.length
    element.classValue = { start, end: start + raw.length, quote }
    element.classes = (quote ? raw.slice(1, -1) : raw).split(/[ \t\n\f\r]+/).filter(Boolean)
  }
  return element
}

const parseMarkup = (html: string): MarkupElement[] => {
  const root: MarkupElement = { name: '#root', start: -1, classes: [], children: [] }
  const open: MarkupElement[] = [root]
  const current = () => open[open.length - 1]!
  const closeTo = (index: number) => { open.length = index }
  const findOpen = (name: string, boundaries: Set<string>) => {
    for (let i = open.length - 1; i > 0; i--) {
      if (open[i]!.name === name) return i
      if (boundaries.has(open[i]!.name)) return -1
    }
    return -1
  }
  const foreign = () => open.some(element => element.name === 'svg' || element.name === 'math')

  TOKEN.lastIndex = 0
  for (let match = TOKEN.exec(html); match; match = TOKEN.exec(html)) {
    const [token, slash, rawName, attributes] = match
    if (rawName === undefined) {
      continue // comment
    }
    const name = rawName.toLowerCase()
    if (slash) {
      const index = findOpen(name, NO_SCOPE_BOUNDARY)
      if (index > 0) closeTo(index)
      continue
    }
    if (CLOSES_P.has(name)) {
      const p = findOpen('p', P_SCOPE_BOUNDARY)
      if (p > 0) closeTo(p)
    }
    if (name === 'li') {
      const li = findOpen('li', LIST_SCOPE_BOUNDARY)
      if (li > 0) closeTo(li)
    }
    const element = readStartTag(name, match.index, attributes!, match.index + 1 + rawName.length)
    current().children.push(element)
    const selfClosing = attributes!.trimEnd().endsWith('/') && (foreign() || name === 'svg' || name === 'math')
    if (VOID.has(name) || selfClosing) {
      continue
    }
    const rawTextEnd = RAW_TEXT_END.get(name)
    if (rawTextEnd) {
      rawTextEnd.lastIndex = match.index + token.length
      TOKEN.lastIndex = rawTextEnd.exec(html)?.index ?? html.length
    }
    open.push(element)
  }
  return root.children
}

// The markup with the classes of every element migrated as above; everything
// else, and every element whose classes stay the same, byte for byte as written.
export const withVuetify4Classes = (html: string): string => {
  // useCmsCarousel replaces these with product carousels, which get their class as written.
  const placeholders = new Set(Array.from(cmsCarouselPlaceholders(html), match => match.index))
  const edits: Array<{ start: number, end: number, text: string }> = []
  const visit = (element: MarkupElement) => {
    // A <template>'s content is not part of the rendered tree: it has no
    // element children there and is never laid out, so it is left as written.
    const children = element.name === 'template' ? [] : element.children
    if (element.classValue && !placeholders.has(element.start)) {
      const migrated = migrateClasses(element.classes, children.map(child => child.classes))
      if (migrated.join(' ') !== element.classes.join(' ')) {
        const quote = element.classValue.quote || '"'
        edits.push({ start: element.classValue.start, end: element.classValue.end, text: `${quote}${migrated.join(' ')}${quote}` })
      }
    }
    children.forEach(visit)
  }
  parseMarkup(html).forEach(visit)
  // In one pass, in document order: splicing each edit into the whole string
  // copies it once per edit.
  edits.sort((a, b) => a.start - b.start)
  const parts: string[] = []
  let cursor = 0
  for (const edit of edits) {
    parts.push(html.slice(cursor, edit.start), edit.text)
    cursor = edit.end
  }
  parts.push(html.slice(cursor))
  return parts.join('')
}
